'use client';
import React, { useState, useEffect } from 'react';

interface WindRoseProps {
  windDirection: number;
  windSpeed: number;
  gustSpeed: number;
  size?: number;
}

// 72 ticks every 5°
const WIND_ROSE_TICKS = Array.from({ length: 72 }, (_, i) => ({
  id: `tick-${i}`,
  angle: i * 5,
  major: i % 18 === 0,  // every 90° — cardinal
  medium: i % 9 === 0,  // every 45°
  small: i % 3 === 0,   // every 15°
}));

export default function WindRose({ windDirection, windSpeed, size = 240 }: WindRoseProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <div className="w-full flex justify-center py-2">
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: '#1D4ED8',
          }}
        />
      </div>
    );
  }

  const cx = size / 2;
  const cy = size / 2;

  // Radii
  const outerRingR = size / 2 - 2;         // outer ring
  const bezelOuterR = outerRingR - 6;       // outer edge of dark bezel
  const bandOuterR = bezelOuterR - 20;      // outer edge of white graduation band
  const bandInnerR = bandOuterR - 18;       // inner edge of white graduation band
  const innerCircleR = bandInnerR - 1;      // dark inner circle

  // Label positions — midway on the bezel
  const labelR = (bezelOuterR + bandOuterR) / 2;

  // Cardinal map
  const cardinalMap: Record<number, string> = { 0: 'N', 90: 'E', 180: 'S', 270: 'O' };
  const degreeLabels = [45, 135, 225, 315];

  // Small yellow circle on the West (270°) side — on the bezel
  const westRad = (270 * Math.PI) / 180;
  const dotR = labelR;
  const dotX = cx + dotR * Math.sin(westRad);
  const dotY = cy - dotR * Math.cos(westRad);

  // Font sizes
  const speedFontSize = Math.max(14, innerCircleR * 0.28);
  const degFontSize = Math.max(13, innerCircleR * 0.25);

  // Direction du vent en degrés météo
  // 0 = Nord, 90 = Est, 180 = Sud, 270 = Ouest
  const direction = windDirection ?? 0;

  // Position du triangle sur le cadran
  const tipRadius = 68;      // pointe vers le centre
  const baseRadius = 96;     // base vers le bord
  const halfWidth = 14;      // demi-largeur de la base

  function degToRad(deg: number) {
    return (deg * Math.PI) / 180;
  }

  function getWindTrianglePoints(dir: number) {
    // Conversion compas -> coordonnées SVG
    // 0° = Nord, sens horaire
    const a = degToRad(dir);

    // Vecteur radial vers l'extérieur
    const ux = Math.sin(a);
    const uy = -Math.cos(a);

    // Vecteur perpendiculaire pour la largeur de la base
    const px = -uy;
    const py = ux;

    // Pointe du triangle (plus près du centre)
    const tipX = cx + ux * tipRadius;
    const tipY = cy + uy * tipRadius;

    // Centre de la base (plus près du bord)
    const baseCenterX = cx + ux * baseRadius;
    const baseCenterY = cy + uy * baseRadius;

    // Deux coins de base
    const leftX = baseCenterX + px * halfWidth;
    const leftY = baseCenterY + py * halfWidth;

    const rightX = baseCenterX - px * halfWidth;
    const rightY = baseCenterY - py * halfWidth;

    return `${tipX},${tipY} ${leftX},${leftY} ${rightX},${rightY}`;
  }

  return (
    <div className="w-full flex justify-center">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        suppressHydrationWarning
        style={{ maxWidth: '100%', height: 'auto' }}
      >
        {/* Outer blue-gray ring — harmonisé avec le thème */}
        <circle cx={cx} cy={cy} r={outerRingR} fill="#2563EB" />

        {/* Dark navy bezel */}
        <circle cx={cx} cy={cy} r={bezelOuterR} fill="#1D4ED8" />

        {/* White graduation band */}
        <circle cx={cx} cy={cy} r={bandOuterR} fill="#f0f6ff" />

        {/* Inner circle — même couleur que le fond de la carte VENT */}
        <circle cx={cx} cy={cy} r={innerCircleR} fill="#eff6ff" />

        {/* Tick marks on graduation band */}
        {WIND_ROSE_TICKS.map((tick) => {
          const rad = (tick.angle * Math.PI) / 180;
          const tickLen = tick.major ? 13 : tick.medium ? 9 : tick.small ? 6 : 3;
          const tickInnerR = bandOuterR - tickLen;
          const x1 = cx + bandOuterR * Math.sin(rad);
          const y1 = cy - bandOuterR * Math.cos(rad);
          const x2 = cx + tickInnerR * Math.sin(rad);
          const y2 = cy - tickInnerR * Math.cos(rad);
          return (
            <line
              key={tick.id}
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke="#93afc8"
              strokeWidth={tick.major ? 2 : tick.medium ? 1.5 : 0.7}
            />
          );
        })}

        {/* Cardinal labels N/E/S/O — jaune doux */}
        {[0, 90, 180, 270].map((deg) => {
          if (deg === 270) return null; // West replaced by yellow dot below
          const rad = (deg * Math.PI) / 180;
          const x = cx + labelR * Math.sin(rad);
          const y = cy - labelR * Math.cos(rad);
          return (
            <text
              key={`cardinal-${deg}`}
              x={x} y={y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={Math.max(10, size * 0.055)}
              fontWeight="900"
              fill="#f5c842"
              fontFamily="Arial, sans-serif"
            >
              {cardinalMap[deg]}
            </text>
          );
        })}

        {/* Small yellow circle on the West side (replacing "O" label) */}
        <circle cx={dotX} cy={dotY} r={Math.max(3, size * 0.018)} fill="#f5c842" />

        {/* Degree labels 45/135/225/315 — bleu pâle / gris bleuté */}
        {degreeLabels.map((deg) => {
          const rad = (deg * Math.PI) / 180;
          const x = cx + labelR * Math.sin(rad);
          const y = cy - labelR * Math.cos(rad);
          return (
            <text
              key={`deg-${deg}`}
              x={x} y={y}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={Math.max(7, size * 0.035)}
              fontWeight="600"
              fill="#b8d0e8"
              fontFamily="Arial, sans-serif"
            >
              {deg}
            </text>
          );
        })}

        {/* Yellow triangular arrow — computed via getWindTrianglePoints */}
        <polygon
          points={getWindTrianglePoints(direction)}
          fill="#f5c842"
          stroke="#d4a017"
          strokeWidth="0.5"
          strokeLinejoin="round"
        />

        {/* Center text: speed (KT) and degrees */}
        <text
          x={cx}
          y={cy - speedFontSize * 0.55}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={speedFontSize}
          fontWeight="900"
          fill="#0f2748"
          fontFamily="Arial, sans-serif"
        >
          {windSpeed} KT
        </text>
        <text
          x={cx}
          y={cy + degFontSize * 0.65}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={degFontSize}
          fontWeight="700"
          fill="#0f2748"
          fontFamily="Arial, sans-serif"
        >
          {windDirection}°
        </text>
      </svg>
    </div>
  );
}